import React from 'react';
import { Filter, Brush, EyeOff, Settings, Info, RefreshCw, CheckCheck } from 'lucide-react';
import { Translations } from '../i18n/translations';
import { useRefreshCountdown } from '../hooks/useRefreshCountdown';
import { SystemStatus, SystemState } from './SystemStatus';

interface ControlBarProps {
  deClickbaitMode: boolean;
  setDeClickbaitMode: (value: boolean) => void;
  cleanMode: boolean;
  setCleanMode: (value: boolean) => void;
  hideClickbait: boolean;
  setHideClickbait: (value: boolean) => void;
  showSettings: boolean;
  onToggleSettings: () => void;
  showAbout: boolean;
  onToggleAbout: () => void;
  onRefresh: () => void;
  onMarkAllRead: () => void;
  hasUnread: boolean;
  loading: boolean;
  hasFeeds: boolean;
  // Cooldown state moved here so the per-minute ticker no longer rerenders App.
  lastRefreshTime: number | null;
  refreshCooldownMs: number;
  systemState: SystemState;
  t: Translations;
}

export const ControlBar: React.FC<ControlBarProps> = ({
  deClickbaitMode,
  setDeClickbaitMode,
  cleanMode,
  setCleanMode,
  hideClickbait,
  setHideClickbait,
  showSettings,
  onToggleSettings,
  showAbout,
  onToggleAbout,
  onRefresh,
  onMarkAllRead,
  hasUnread,
  loading,
  hasFeeds,
  lastRefreshTime,
  refreshCooldownMs,
  systemState,
  t,
}) => {
  const { canRefresh, minutesUntilRefresh } = useRefreshCountdown(
    lastRefreshTime,
    refreshCooldownMs,
  );
  const refreshDisabled = !hasFeeds || loading || !canRefresh;
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t-2 border-gray-300 dark:border-gray-600 shadow-lg z-40" role="navigation" aria-label="Main controls">
      <div className="px-3 sm:px-6 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-1 sm:gap-3">
          {/* Left side: All filter controls */}
          <div className="flex items-center gap-1 sm:gap-2" role="group" aria-label="Article filters">
            <button
              onClick={() => setDeClickbaitMode(!deClickbaitMode)}
              className={`p-2 sm:px-4 sm:py-2 text-sm font-semibold border-2 inline-flex flex-row items-center gap-1 whitespace-nowrap ${
                deClickbaitMode ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'bg-white text-black border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600'
              }`}
              title={t.flagClickbait}
              aria-pressed={deClickbaitMode}
              aria-label={t.flagClickbait}
            >
              <Filter className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t.flagClickbait}</span>
            </button>
            <button
              onClick={() => setCleanMode(!cleanMode)}
              className={`p-2 sm:px-4 sm:py-2 text-sm font-semibold border-2 inline-flex flex-row items-center gap-1 whitespace-nowrap ${
                cleanMode ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'bg-white text-black border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600'
              }`}
              title={t.clean}
              aria-pressed={cleanMode}
              aria-label={t.clean}
            >
              <Brush className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t.clean}</span>
            </button>
            <button
              onClick={() => setHideClickbait(!hideClickbait)}
              className={`p-2 sm:px-4 sm:py-2 text-sm font-semibold border-2 inline-flex flex-row items-center gap-1 whitespace-nowrap ${
                hideClickbait ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'bg-white text-black border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600'
              }`}
              title={t.hideClickbait}
              aria-pressed={hideClickbait}
              aria-label={t.hideClickbait}
            >
              <EyeOff className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t.hideClickbait}</span>
            </button>
          </div>

          {/* Right side: System status, Mark-all read, Refresh, About and Settings */}
          <div className="flex items-center gap-1 sm:gap-2" role="group" aria-label="Actions">
            <SystemStatus state={systemState} t={t} />
            <button
              onClick={onMarkAllRead}
              disabled={!hasUnread}
              className="p-2 sm:px-4 sm:py-2 text-sm font-semibold border-2 inline-flex flex-row items-center gap-1 whitespace-nowrap bg-white text-black border-gray-300 hover:bg-gray-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
              title={t.markAllAsRead}
              aria-label={t.markAllAsRead}
            >
              <CheckCheck className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t.markAllAsRead}</span>
            </button>
            <button
              onClick={onRefresh}
              disabled={refreshDisabled}
              className="p-2 sm:px-4 sm:py-2 text-sm font-semibold border-2 inline-flex flex-row items-center gap-1 whitespace-nowrap bg-blue-700 text-white border-blue-700 hover:bg-blue-800 disabled:bg-gray-400 disabled:border-gray-400 disabled:cursor-not-allowed"
              title={!canRefresh ? `${t.refreshAvailableIn} ${minutesUntilRefresh} ${t.minutes}` : t.refreshAll}
              aria-label={!canRefresh ? `${t.refreshAvailableIn} ${minutesUntilRefresh} ${t.minutes}` : t.refreshAll}
            >
              <RefreshCw className={`w-4 h-4 shrink-0 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              {/* Mobile: show small countdown number when disabled */}
              {!canRefresh && minutesUntilRefresh > 0 && (
                <span className="sm:hidden text-xs">{minutesUntilRefresh}</span>
              )}
              {/* Desktop: show full text */}
              <span className="hidden sm:inline">
                {!canRefresh && minutesUntilRefresh > 0
                  ? `${minutesUntilRefresh}m`
                  : t.refreshAll}
              </span>
            </button>
            <button
              onClick={onToggleAbout}
              className={`p-2 sm:px-4 sm:py-2 text-sm font-semibold border-2 inline-flex flex-row items-center gap-1 whitespace-nowrap ${
                showAbout ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'bg-white text-black border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600'
              }`}
              title={t.about}
              aria-expanded={showAbout}
              aria-label={t.about}
            >
              <Info className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t.about}</span>
            </button>
            <button
              onClick={onToggleSettings}
              className={`p-2 sm:px-4 sm:py-2 text-sm font-semibold border-2 inline-flex flex-row items-center gap-1 whitespace-nowrap ${
                showSettings ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white' : 'bg-white text-black border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600'
              }`}
              title={t.settings}
              aria-expanded={showSettings}
              aria-label={t.settings}
            >
              <Settings className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t.settings}</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};
